import React from 'react';
import { ArrowUpRight } from 'lucide-react';
import type { Commerce } from './ShopCommerce';
import { useStoreCollections } from '../elements/render/useStoreCatalog';
import { shopWords, useShopWords } from './shopWords';

// The cart's states other than its lines, shared by the drawer and a checkout page: nothing in it yet, still
// checking prices, or a read that failed and can be tried again.

/** How many of the store's collections an empty cart suggests. */
const SUGGESTED = 4;

// Offers the store's own collections as rows with their picture and an arrow, as Sleek's empty cart does.
const CollectionRows: React.FC = () => {
  const read = useStoreCollections(SUGGESTED);
  const words = useShopWords();
  const rows = read.data ?? [];
  if (rows.length === 0) return null;
  return (
    <>
      <p className="xe-cart-empty-hint">{words.cartEmptyHint}</p>
      <ul className="xe-cart-collections">
        {rows.map((row) => (
          <li key={row.id}>
            <a href={`/collections/${encodeURIComponent(row.slug)}`}>
              {row.imageUrl ? <img src={row.imageUrl} alt="" /> : <span className="xe-cart-collections-blank" />}
              <span dir="auto">{row.title}</span>
              <ArrowUpRight size={18} aria-hidden="true" />
            </a>
          </li>
        ))}
      </ul>
    </>
  );
};

// Says the cart is empty and offers the way back to the shop: its collections, then closing the drawer or the catalogue.
export const EmptyCart: React.FC<{ onContinue?: () => void }> = ({ onContinue }) => {
  const words = useShopWords();
  return (
    <div className="xe-cart-empty">
      <p className="xe-cart-empty-title">{words.cartEmpty}</p>
      <CollectionRows />
      {onContinue
        ? <button type="button" className="xe-cart-primary" onClick={onContinue}>{words.continueShopping}</button>
        : <a className="xe-cart-primary" href="/collections/all">{words.continueShopping}</a>}
    </div>
  );
};

// Says what the cart is waiting on, or what went wrong reading it and how to try again.
export const CartStatus: React.FC<{ commerce: Commerce }> = ({ commerce }) => {
  if (commerce.error) {
    return (
      <div className="xe-cart-problem" role="alert">
        <p>{commerce.error}</p>
        <button type="button" className="xe-cart-secondary" onClick={commerce.retry}>{shopWords(commerce.language).tryAgain}</button>
      </div>
    );
  }
  return commerce.loading ? <p className="xe-cart-status" role="status">{shopWords(commerce.language).checkingPrices}</p> : null;
};
